import { Link, useNavigate, useParams } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Seo } from "@/components/seo/Seo";
import { CITIES } from "../data/landingConfig";
import { ArrowRight, Check, Globe, LayoutDashboard, MapPin, TrendingUp } from "lucide-react";

const SERVICE_LINKS = [
  { slug: "pulizie-appartamenti-vacanze", label: "Pulizie appartamenti vacanze" },
  { slug: "sanificazione-attivita-commerciali", label: "Sanificazione attività commerciali" },
  { slug: "pulizie-post-cantiere", label: "Pulizie post-cantiere" },
  { slug: "pulizie-uffici-aziende", label: "Pulizie uffici e aziende" },
  { slug: "pulizie-supermercati-ristoranti", label: "Pulizie supermercati e ristoranti" },
  { slug: "pulizie-enti-pubblici", label: "Pulizie per enti pubblici" },
];

export default function LandingAzienda() {
  const navigate = useNavigate();
  const { città, city: cityParam } = useParams<{ città?: string; city?: string }>();
  const city = CITIES[città ?? cityParam ?? ""];

  if (!city) {
    return (
      <main className="container mx-auto px-6 py-16 text-center">
        <Seo title="Pagina non trovata | Halomora" canonicalPath="/for-businesses" />
        <h1 className="text-2xl font-bold text-gray-900">Pagina non trovata</h1>
        <p className="mt-2 text-gray-600">La città richiesta non è disponibile.</p>
        <Button className="mt-6" onClick={() => navigate("/for-businesses")}>Vai a Per le aziende</Button>
      </main>
    );
  }

  const cityName = city.nomeEsteso;
  const canonicalPath = `/aziende/pulizie/${city.slug}`;
  const title = `Sito Web, Gestionale e Clienti per Imprese di Pulizie a ${cityName} | Halomora`;
  const description = `Soluzioni per imprese di pulizie a ${cityName}: sito web professionale, gestionale prenotazioni, calendario, pagamenti e clienti dal marketplace locale. Meno caos operativo, più richieste qualificate.`;

  const pillars = [
    {
      icon: Globe,
      title: "Un sito web professionale",
      text: `Un sito su misura per la tua impresa di pulizie a ${cityName}, ottimizzato per ricerche locali come servizi, zone servite e preventivi, con form collegato a Halomora.`,
      ctaLabel: "Guarda gli esempi",
      to: "/for-businesses/booking-system-demo",
    },
    {
      icon: LayoutDashboard,
      title: "Un gestionale completo",
      text: "Software gestionale per prenotazioni, calendario, personale, preventivi, fatture e pagamenti in un unico posto. Basta messaggi sparsi tra WhatsApp, telefono ed Excel.",
      ctaLabel: "Scopri il profilo gratuito",
      to: "/for-businesses",
    },
    {
      icon: TrendingUp,
      title: "Più clienti e più vendite",
      text: `Ricevi richieste dal marketplace e da chi cerca imprese di pulizie a ${cityName} su Google. Cresci senza costi fissi: commissione a partire dal 5% solo sugli ordini ricevuti.`,
      ctaLabel: "Registrati gratis",
      to: "/auth?type=company",
    },
  ];

  const steps = [
    "Registrati con email o Google e conferma l'account.",
    "Presenta l'azienda: nome, zone servite, servizi e foto. P.IVA opzionale.",
    "Aggiungi servizi e prezzi (la commissione Halomora è chiara dall'inizio).",
    "Attiva i pagamenti e inizia a ricevere richieste nella tua zona.",
  ];

  const benefits = [
    "Profilo visibile a chi cerca pulizie nella tua zona",
    "Richieste con servizio, zona e contatto già ordinati",
    "Calendario e personale coordinati in un posto",
    "Pagamenti tracciati e commissione trasparente",
  ];

  const faqs = [
    {
      q: `Quanto costa iscriversi su Halomora per un'impresa di pulizie a ${cityName}?`,
      a: "L'iscrizione è gratuita e non ha canone mensile. Paghi una commissione a partire dal 5% solo sugli ordini ricevuti tramite il marketplace: nessun costo se non lavori.",
    },
    {
      q: `Potete realizzare un sito web per la mia impresa di pulizie a ${cityName}?`,
      a: "Sì. Realizziamo un sito professionale per la tua impresa, con pagine servizi e zone servite, form preventivo e collegamento al backend Halomora per richieste e prenotazioni. È un servizio opzionale su preventivo; il profilo nel marketplace resta gratuito.",
    },
    {
      q: "Cosa include il gestionale per imprese di pulizie?",
      a: "Gestione di richieste e prenotazioni, calendario e personale, preventivi, fatture, pagamenti tracciati e una dashboard su vendite e servizi più richiesti.",
    },
    {
      q: `Posso lavorare anche nelle zone vicine a ${cityName}?`,
      a: `Sì. Puoi indicare le aree di copertura (${city.zoneVicine}) così i clienti ti trovano anche fuori città.`,
    },
    {
      q: "I clienti sono garantiti?",
      a: "No. Il volume dipende da zona, stagionalità e qualità del profilo. Halomora aumenta visibilità e organizzazione, non promette un fatturato fisso.",
    },
  ];

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      serviceType: "Marketplace e strumenti per imprese di pulizie",
      provider: { "@type": "Organization", name: "Halomora", url: "https://halomora.com" },
      areaServed: { "@type": "City", name: cityName },
      url: `https://halomora.com${canonicalPath}`,
      offers: {
        "@type": "Offer",
        name: "Profilo gratuito",
        price: "0",
        priceCurrency: "EUR",
        description: "Profilo nel marketplace, gestionale prenotazioni e pagamenti. Commissione dal 5% solo sugli ordini ricevuti.",
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
    },
  ];

  return (
    <div className="min-h-screen app-page">
      <Seo title={title} description={description} canonicalPath={canonicalPath} jsonLd={jsonLd} />

      <section className="relative overflow-hidden bg-slate-950">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(59,130,246,0.28),transparent_34%),radial-gradient(circle_at_top_right,rgba(16,185,129,0.18),transparent_30%),linear-gradient(180deg,rgba(15,23,42,0.95),rgba(2,6,23,0.98))]" />
        <div className="relative container mx-auto px-6 py-14 md:py-20">
          <div className="max-w-3xl">
            <div className="text-sm text-slate-300">
              <Link className="text-slate-200 hover:text-white" to="/for-businesses">Per le aziende</Link>
              <span className="mx-2 text-slate-500">/</span>
              <span className="text-white">{cityName}</span>
            </div>
            <Badge className="mt-4 border border-white/15 bg-white text-primary hover:bg-white">
              Imprese di pulizie a {cityName}
            </Badge>
            <h1 className="mt-4 text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight tracking-tight">
              Sito web, gestionale e nuovi clienti per la tua impresa di pulizie a {cityName}
            </h1>
            <p className="mt-5 text-lg md:text-xl text-slate-200 leading-relaxed">
              Halomora ti dà un sito professionale, un gestionale per prenotazioni, personale e pagamenti, e clienti dal marketplace locale. Meno caos operativo, più richieste qualificate, nessun canone mensile per iniziare.
            </p>
            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <Button size="lg" className="shadow-xl" onClick={() => navigate("/auth?type=company")}>
                Registrati gratis
              </Button>
              <Button size="lg" variant="outline" className="bg-white/5 border-white/20 text-white hover:bg-white/12" onClick={() => navigate("/for-businesses#sales-contact")}>
                Parla con noi
              </Button>
            </div>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-6 py-12 md:py-16 space-y-16">
        <section className="space-y-6">
          <div className="max-w-3xl">
            <Badge variant="outline" className="mb-3">Tre modi per crescere</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight">Tutto quello che serve alla tua impresa, in un posto solo</h2>
          </div>
          <div className="grid gap-5 md:grid-cols-3">
            {pillars.map((p) => (
              <Card key={p.title} className="border-gray-200 shadow-card h-full">
                <CardContent className="p-6 flex flex-col h-full">
                  <div className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary mb-4">
                    <p.icon className="h-5 w-5" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900">{p.title}</h3>
                  <p className="mt-2 text-sm text-gray-600 leading-relaxed flex-1">{p.text}</p>
                  <Link to={p.to} className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline">
                    {p.ctaLabel} <ArrowRight className="h-4 w-4" />
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        <section className="grid gap-6 lg:grid-cols-2 lg:items-center">
          <div className="space-y-4">
            <Badge variant="outline"><MapPin className="h-3.5 w-3.5 mr-1 inline" /> Lavorare a {cityName}</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight">Pensato per il territorio in cui lavori</h2>
            <p className="text-gray-600 leading-relaxed">
              {cityName} significa {city.contestoLocale}. {city.specificita}: con Halomora organizzi richieste, zone e disponibilità in modo ordinato, così rispondi più in fretta e perdi meno lavoro.
            </p>
            <p className="text-sm text-gray-600"><strong>Zone coperte:</strong> {city.zoneVicine}.</p>
          </div>
          <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-emerald-500/5 shadow-card">
            <CardContent className="p-6 space-y-3">
              {benefits.map((line) => (
                <div key={line} className="flex items-start gap-3 text-sm text-gray-700">
                  <Check className="h-4 w-4 shrink-0 mt-0.5 text-primary" strokeWidth={2.5} />
                  <span>{line}</span>
                </div>
              ))}
            </CardContent>
          </Card>
        </section>

        <section className="space-y-6">
          <div className="max-w-3xl">
            <Badge variant="outline" className="mb-3">Servizi richiesti a {cityName}</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight">Cosa puoi offrire e farti trovare per</h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {SERVICE_LINKS.map((s) => (
              <Link key={s.slug} to={`/servizi/${s.slug}/${city.slug}`} className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:border-primary/40 hover:text-primary transition-colors">
                {s.label} a {cityName}
              </Link>
            ))}
          </div>
        </section>

        <section className="space-y-6">
          <div className="max-w-3xl">
            <Badge variant="outline" className="mb-3">In 5 minuti</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight">Come iniziare</h2>
          </div>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {steps.map((step, i) => (
              <div key={step} className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                <div className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-primary text-white font-semibold text-sm">{i + 1}</div>
                <p className="mt-4 text-sm text-gray-600 leading-relaxed">{step}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="relative overflow-hidden rounded-[1.75rem] border border-primary/25 shadow-[0_28px_70px_-34px_rgba(37,99,235,0.4)]">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(59,130,246,0.35),transparent_38%),linear-gradient(135deg,hsl(223,72%,38%)_0%,hsl(217,78%,32%)_48%,hsl(266,65%,36%)_100%)]" />
          <div className="relative px-6 py-10 md:py-12 text-center space-y-5">
            <h2 className="text-2xl md:text-3xl font-bold text-white tracking-tight">Inizia gratis a {cityName}</h2>
            <p className="text-slate-100/90 max-w-2xl mx-auto">Le prime imprese iscritte ricevono visibilità prioritaria nelle ricerche della zona. Nessun rischio iniziale: paghi solo quando il lavoro arriva.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button size="lg" variant="hero" onClick={() => navigate("/auth?type=company")}>Registrati gratis</Button>
              <Button size="lg" variant="outline" className="border-white/35 bg-white/10 text-white hover:bg-white/20 hover:text-white" onClick={() => navigate("/for-businesses")}>Scopri di più</Button>
            </div>
          </div>
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 tracking-tight">Domande frequenti — aziende a {cityName}</h2>
          <div className="space-y-3">
            {faqs.map((f) => (
              <details key={f.q} className="rounded-lg border border-gray-200 p-4 bg-white">
                <summary className="cursor-pointer font-medium text-gray-900">{f.q}</summary>
                <p className="mt-2 text-gray-600 leading-relaxed text-sm">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Imprese di pulizie in altre città</h2>
          <div className="flex flex-wrap gap-2">
            {Object.values(CITIES).map((c) => (
              <Link key={c.slug} to={`/aziende/pulizie/${c.slug}`} className="rounded-full border border-slate-200 bg-white px-4 py-1.5 text-sm font-medium text-slate-700 hover:border-primary/40">
                {c.nomeEsteso}
              </Link>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
